import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import FormatListNumberedOutlinedIcon from '@mui/icons-material/FormatListNumberedOutlined';
import { Typography, Box } from '@mui/material';
import { ConfirmDialog, EmptyState, PageHeader, useNotify } from '@routes-plan/ui';
import { FilterBar, OfficeRouteFields, Section } from '../components/ProcessFilters';
import { useSelection } from '../../../components/context/SelectionContext';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import SortableVisitItem from './SortableVisitItem'; // Componente para cada fila
import { Route } from '../../../models/route';
import { Loan } from '../../../models/loan';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';

interface ShowAlerts {
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

const VisitOrderEditor: React.FC<ShowAlerts> = ({ showLoading, hideLoading }) => {
    const t = useT();
    const notify = useNotify();
    const showAlert = (message: string, severity: 'success' | 'info' | 'warning' | 'error' = 'info') => notify[severity](message);
    useEffect(() => {
        setPageTitle('menu.processes.visit-order');
    }, []);
    const { canUpdate } = useResourcePermissions('/processes/visit-order');
    const [loans, setLoans] = useState<Loan[]>([]);
    const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
    const [offices, setOffices] = useState<{ id: number; name: string }[]>([]);
    const {
        office_id: officeId,
        setOffice_id: setOfficeId,
        collect_id: routeId,
        setCollect_id: setRouteId,
    } = useSelection();
    const [open, setOpen] = useState(false); // Estado para controlar la apertura del modal

    // Cargar oficinas y datos relacionados con officeId
    useEffect(() => {
        const fetchData = async () => {
            showLoading();
            try {
                const officesResponse = axios.get('offices');
                const officeRoutesResponse = officeId ? axios.get(`offices/${officeId}/routes`) : null;

                const [officesData, officeRoutesData] = await Promise.all([
                    officesResponse,
                    officeRoutesResponse,
                ]);

                if (officesData) setOffices(officesData.data.data);
                if (officeRoutesData) setOfficeRoutes(officeRoutesData.data);
                setLoans([]);
            } catch (error) {
                //console.log('Error fetching data:', 'error');
            } finally {
                hideLoading();
            }
        };

        fetchData();
    }, [officeId]);

    // Cargar clientes solo cuando routeId tenga un valor válido
    useEffect(() => {
        const fetchRouteLoans = async () => {
            if (!routeId) {
                setLoans([]);
                return;
            }
            showLoading();
            try {
                const { data } = await axios.get(`routes/${routeId}/loans/clients`);
                setLoans(data.data);
                showAlert(t('visitOrderEditor.loadedOk'), 'info');
            } catch (error) {
                showAlert(t('visitOrderEditor.loadError'), 'error');
            } finally {
                hideLoading();
            }
        };

        fetchRouteLoans();
    }, [routeId]);

    // Configuración de sensores para el drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Manejar el evento de arrastrar y soltar
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            setLoans((loans) => {
                const oldIndex = loans.findIndex((loan) => loan.id === active.id);
                const newIndex = loans.findIndex((loan) => loan.id === over?.id);
                return arrayMove(loans, oldIndex, newIndex);
            });
        }
    };

    // Función para abrir el modal de confirmación
    const handleOpenModal = () => {
        if (!canUpdate) {
            showAlert(t('visitOrderEditor.noPermissionSave'), 'warning');
            return;
        }
        setOpen(true);
    };

    // Función para cerrar el modal
    const handleCloseModal = () => {
        setOpen(false);
    };

    // Función para confirmar y enviar la información
    const handleConfirm = async () => {
        setOpen(false); 
        await submit();
    };

    // Función para enviar la información a la API
    const submit = async () => {
        const formData = loans.map((loan, index) => ({
            loanId: loan.id,
            position: index + 1,
        }));
        try {
            showLoading();
            await axios.post(`visit-orders/positions`, { parsedNewPositionRoute: formData });
            showAlert(t('visitOrderEditor.savedOk'), 'success');
        } catch (error) {
            showAlert(t('visitOrderEditor.saveError'), 'error');
        } finally {
            hideLoading();
        }
    };

    const selectedRouteName = officeRoutes.find((r) => String(r.id) === String(routeId))?.name;

    return (
        <HeaderResponsive>
            <PageHeader
                title={t('visitOrderEditor.title')}
                description={t('visitOrderEditor.subtitle')}
                actions={
                    <PermissionButton
                        allowed={canUpdate}
                        deniedTooltip={t('visitOrderEditor.noPermissionSaveShort')}
                        variant="contained"
                        startIcon={<SaveOutlinedIcon />}
                        onClick={handleOpenModal}
                        disabled={loans.length === 0}
                    >
                        {t('common.save')}
                    </PermissionButton>
                }
            />
            <FilterBar>
                <OfficeRouteFields
                    idPrefix="visit-order-editor"
                    offices={offices}
                    routes={officeRoutes}
                    officeId={officeId}
                    routeId={routeId}
                    onOfficeChange={(v) => setOfficeId(v)}
                    onRouteChange={(v) => setRouteId(v)}
                />
            </FilterBar>

            <Section
                title={selectedRouteName ? t('visitOrderEditor.listTitleRoute', { route: selectedRouteName }) : t('visitOrderEditor.listTitle')}
                action={loans.length > 0 ? <Typography variant="body2" color="text.secondary">{t('visitOrderEditor.count', { count: loans.length })}</Typography> : undefined}
            >
                {loans.length > 0 ? (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            {t('visitOrderEditor.dragHint')}
                        </Typography>
                        <Box
                            sx={{ maxHeight: 'min(70vh, 720px)', overflowY: 'auto', pr: 0.5 }}
                            tabIndex={0}
                            role="region"
                            aria-label={t('visitOrderEditor.listTitle')}
                        >
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                                modifiers={[restrictToVerticalAxis]}
                            >
                                <SortableContext items={loans} strategy={verticalListSortingStrategy}>
                                    {loans.map((loan, index) => (
                                        <SortableVisitItem key={loan.id} id={loan.id} dragLabel={t('visitOrderEditor.dragLabel', { name: loan.client.fullName })}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                                <Box
                                                    sx={{
                                                        flex: '0 0 auto',
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: '50%',
                                                        display: 'grid',
                                                        placeItems: 'center',
                                                        bgcolor: 'action.selected',
                                                        typography: 'body2',
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {index + 1}
                                                </Box>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="body2" fontWeight={600} noWrap title={loan.client.fullName}>
                                                        {loan.client.fullName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" noWrap component="p">
                                                        {loan.client.idNumber}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </SortableVisitItem>
                                    ))}
                                </SortableContext>
                            </DndContext>
                        </Box>
                    </>
                ) : (
                    <EmptyState
                        dense
                        icon={<FormatListNumberedOutlinedIcon />}
                        title={t('visitOrderEditor.noRecords')}
                        description={routeId ? undefined : t('visitOrderEditor.selectToStart')}
                    />
                )}
            </Section>

            <ConfirmDialog
                open={open}
                variant="info"
                title={t('visitOrderEditor.confirmTitle')}
                message={t('visitOrderEditor.confirmMessage')}
                confirmLabel={t('common.save')}
                cancelLabel={t('common.cancel')}
                onConfirm={handleConfirm}
                onCancel={handleCloseModal}
            />
        </HeaderResponsive>
    );
};

export default VisitOrderEditor;
